<template>
  <div>
    <Navbar />

    <main class="auth-page">
      <div class="auth-bg">
        <div class="auth-bg-overlay"></div>
      </div>

      <div class="auth-container">
        <div class="auth-card">

          <!-- ── Loading state ── -->
          <template v-if="loading">
            <div class="verify-state">
              <div class="spinner-wrap">
                <div class="spinner-border text-primary spinner-lg" role="status"></div>
              </div>
              <h3 class="auth-title">Activating your account...</h3>
              <p class="auth-sub">Please wait while we verify your email address.</p>
            </div>
          </template>

          <!-- ── Success state ── -->
          <template v-else-if="status === 200">
            <div class="verify-state">
              <div class="result-icon-wrap result-icon-wrap--success">
                <font-awesome-icon icon="check-circle" class="result-icon" />
              </div>
              <h3 class="auth-title auth-title--success">Account Activated!</h3>
              <p class="auth-sub">{{ response }}</p>
              <template v-if="requiresSetup">
                <p class="auth-sub" style="margin-top:-8px;">
                  Your account is being set up in the background. Complete the short onboarding form to personalise your workspace — it will be ready by the time you finish.
                </p>
                <button class="submit-btn" style="justify-content:center; margin-top:8px;" @click="goToSetup">
                  <font-awesome-icon icon="arrow-right" class="me-1" /> Complete Account Setup
                </button>
              </template>
              <template v-else>
                <p class="auth-sub" style="margin-top:-8px;">You can now sign in and start using Mapiibs.</p>
                <router-link to="/auth/login" class="submit-btn" style="text-decoration:none; justify-content:center; margin-top:8px;">
                  <font-awesome-icon icon="arrow-right" class="me-1" /> Go to Login
                </router-link>
              </template>
            </div>
          </template>

          <!-- ── Failure state ── -->
          <template v-else>
            <div class="verify-state">
              <div class="result-icon-wrap result-icon-wrap--error">
                <font-awesome-icon icon="times-circle" class="result-icon" />
              </div>
              <h3 class="auth-title auth-title--error">Activation Failed</h3>
              <p class="auth-sub">{{ response }}</p>
              <p class="auth-sub" style="margin-top:-8px;">The link may have expired or already been used.</p>
              <div class="action-stack">
                <router-link to="/auth/send-email-verifcation" class="submit-btn submit-btn--outline" style="text-decoration:none; justify-content:center;">
                  <font-awesome-icon icon="envelope" class="me-1" /> Resend Verification Email
                </router-link>
                <router-link to="/auth/login" class="back-link" style="text-decoration:none;">
                  <font-awesome-icon icon="arrow-left" class="me-1" /> Back to Login
                </router-link>
              </div>
            </div>
          </template>

        </div>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script>
import { ref } from 'vue';
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router';
import { onMounted } from 'vue';
import Navbar from '../components/Navbar';
import Footer from '../components/footer';

export default {
  name: 'EmailVerification',

  components: { Navbar, Footer },

  setup() {
    const route        = useRoute();
    const router       = useRouter();
    const loading      = ref(true);
    const response     = ref('');
    const status       = ref(null);
    const requiresSetup = ref(false);
    const setupToken   = ref(null);

    const verifyEmail = async () => {
      loading.value = true;
      try {
        const { data, status: s } = await axios.post('/verify-email', {
          verification_code: route.params.token,
        });
        response.value = data.message;
        status.value   = s;

        // Check if this is a super user who needs to complete setup
        if (s === 200 && data.data?.is_super && !data.data?.setup_completed) {
          requiresSetup.value = true;
          setupToken.value    = data.data.setup_token;
        }
      } catch (err) {
        status.value   = err.response?.status ?? 500;
        response.value = err.response?.data?.message ?? 'Something went wrong. Please try again.';
      } finally {
        loading.value = false;
      }
    };

    const goToSetup = () => {
      if (setupToken.value) {
        localStorage.setItem('setup_token', setupToken.value);
      }
      router.push('/auth/account-setup');
    };

    onMounted(verifyEmail);

    return { loading, response, status, requiresSetup, goToSetup };
  },
};
</script>

<style scoped>
/* ── Page shell ──────────────────────────────────────────────────────── */
.auth-page {
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: url('/static/img/banner-bg-5.jpg') center/cover no-repeat;
}
.auth-bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(5,27,53,.96) 0%, rgba(0,74,173,.82) 100%);
}

.auth-container {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 40px 24px;
  min-height: 100vh;
}

/* ── Card ────────────────────────────────────────────────────────────── */
.auth-card {
  background: #fff;
  border-radius: 16px;
  padding: 52px 40px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 24px 60px rgba(0,0,0,.22);
}

/* ── Verify states ───────────────────────────────────────────────────── */
.verify-state { display:flex; flex-direction:column; align-items:center; text-align:center; gap:14px; }

/* ── Loading spinner ─────────────────────────────────────────────────── */
.spinner-wrap { margin-bottom:4px; }
.spinner-lg   { width:3.5rem; height:3.5rem; }

/* ── Result icons ────────────────────────────────────────────────────── */
.result-icon-wrap {
  width: 76px; height: 76px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 4px;
}
.result-icon-wrap--success { background: rgba(22,163,74,.1); color:#16a34a; }
.result-icon-wrap--error   { background: rgba(224,53,53,.08); color:#e03535; }
.result-icon { font-size:32px; }

/* ── Text ────────────────────────────────────────────────────────────── */
.auth-title           { font-size:22px; font-weight:800; color:#1a2b45; margin:0; }
.auth-title--success  { color:#15803d; }
.auth-title--error    { color:#e03535; }
.auth-sub             { font-size:14px; color:#7a90b0; margin:0; line-height:1.55; max-width:320px; }
.me-1 { margin-right:4px; }

/* ── Action stack ────────────────────────────────────────────────────── */
.action-stack { display:flex; flex-direction:column; align-items:center; gap:12px; width:100%; margin-top:4px; }

/* ── Buttons ─────────────────────────────────────────────────────────── */
.submit-btn {
  width: 100%;
  padding: 13px;
  background: #004AAD;
  color: #fff;
  border: none;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s, transform .1s;
  display: flex;
  align-items: center;
  gap: 8px;
}
.submit-btn:hover:not(:disabled) { background:#003b8e; }
.submit-btn:active:not(:disabled) { transform:scale(.98); }

.submit-btn--outline {
  background: transparent;
  color: #004AAD;
  border: 1.5px solid #004AAD;
}
.submit-btn--outline:hover { background: rgba(0,74,173,.06); }

.back-link {
  display: inline-flex; align-items: center; gap: 6px;
  color: #7a90b0; font-size: 13px; font-weight: 600;
  transition: color .15s;
}
.back-link:hover { color: #004AAD; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .auth-card      { padding:36px 20px; }
  .auth-container { padding:24px 16px; }
}
</style>